import { useMemo } from 'react'
import { uniqueOptions } from 'Utils/profileAccess.utils'
import { isSelectAll } from 'Components/SbJdCreationDrawerV2/helpers'

// ─────────────────────────────────────────────────────────────────────────────
// OPTION BUILDERS
// Pure functions — no hooks, safe to call inside or outside React
// ─────────────────────────────────────────────────────────────────────────────

// job_family_name is not on the requisition object directly;
// it lives in orgMetaData.jobs keyed by job_code — Map for O(1) lookup
export const buildRoleIdOptions = (requisitionViewData = [], orgMetaData) => {
  const jobsMap = new Map((orgMetaData?.jobs ?? []).map((j) => [j.job_code, j]))

  return requisitionViewData.map((req) => {
    const job = jobsMap.get(req.job_code)
    const label = job?.job_family_name || req?.job_title || req?.job_title_for_jd || ''
    return {
      value: req.sb_requisition_number,
      label: `${req.sb_requisition_number} - ${label}`,
    }
  })
}

// All legal entities come from the top level of org structure
export const buildLegalEntityOptions = (structure = []) =>
  dropRedundantAll(structure.map((item) => ({ value: item.legal_entity, label: item.legal_entity })))

// ─────────────────────────────────────────────────────────────────────────────
// CASCADING OPTIONS  (Legal → BU → Function → SubFunction)
// ─────────────────────────────────────────────────────────────────────────────

// "ALL" is only meaningful when it stands for more than one thing. With a single
// real option the sentinel is a duplicate of it, so hide it. With zero real
// options the sentinel is the only selectable value — keep it.
const dropRedundantAll = (options = []) => {
  const realOptions = options.filter((o) => !isSelectAll(o.value))
  return realOptions.length === 1 ? realOptions : options
}

export const getChainedOptions = (structure = [], { legalEntities, businessUnits, functions }) => {
  const filteredLegal = structure.filter((item) => legalEntities.includes(item.legal_entity))

  const businessUnitOptions = dropRedundantAll(uniqueOptions(
    filteredLegal.flatMap((item) =>
      item.business_units.map((bu) => ({ value: bu.business_unit, label: bu.business_unit }))
    )
  ))

  const selectedBUObjects = filteredLegal
    .flatMap((item) => item.business_units)
    .filter((bu) => businessUnits.includes(bu.business_unit))

  const functionOptions = dropRedundantAll(uniqueOptions(
    selectedBUObjects.flatMap((bu) =>
      bu.functions.map((func) => ({ value: func.function, label: func.function }))
    )
  ))

  const selectedFunctionObjects = selectedBUObjects
    .flatMap((bu) => bu.functions)
    .filter((func) => functions.includes(func.function))

  const subFunctionOptions = dropRedundantAll(uniqueOptions(
    selectedFunctionObjects.flatMap((func) =>
      func.sub_functions.map((sub) => ({ value: sub.sub_function, label: sub.sub_function }))
    )
  ))

  return { businessUnitOptions, functionOptions, subFunctionOptions }
}

// ─────────────────────────────────────────────────────────────────────────────
// SELECTION HELPERS
// ─────────────────────────────────────────────────────────────────────────────

// Full Org — returns every real org value as arrays, never a sentinel string
export const getAllOrgValues = (structure = []) => {
  const legalEntities = structure.map((item) => item.legal_entity)

  const businessUnits = uniqueOptions(
    structure.flatMap((item) =>
      item.business_units.map((bu) => ({ value: bu.business_unit, label: bu.business_unit }))
    )
  ).map((o) => o.value)

  const functions = uniqueOptions(
    structure.flatMap((item) =>
      item.business_units.flatMap((bu) =>
        bu.functions.map((func) => ({ value: func.function, label: func.function }))
      )
    )
  ).map((o) => o.value).filter((v) => !isSelectAll(v))

  const subFunctions = uniqueOptions(
    structure.flatMap((item) =>
      item.business_units.flatMap((bu) =>
        bu.functions.flatMap((func) =>
          func.sub_functions.map((sub) => ({ value: sub.sub_function, label: sub.sub_function }))
        )
      )
    )
  ).map((o) => o.value).filter((v) => !isSelectAll(v))

  return {
    legal_entities: legalEntities,
    business_units: businessUnits,
    functions,
    sub_functions: subFunctions,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PAIC OWNERSHIP CONFLICT
// ─────────────────────────────────────────────────────────────────────────────

// update-emp-access reuses 7214 (PAIC's NOT_PROJECT_OWNER) to mean "the new scope
// would hide JDs on PAIC projects this user owns" — only valid alongside a 409.
// The same 409 also lists Jobs Library JDs (reason SCOPE_CHANGE_BLOCKED, blocked_jds):
// ones the user owns, is the delegate on, is shared on or approves (Ownership design, part 8).
const OWNERSHIP_CONFLICT_STATUS = 409
const OWNERSHIP_CONFLICT_ERROR_CODE = 7214

// What each role is called in the conflict popup
export const JD_CONFLICT_ROLE_LABELS = {
  owner: 'Owner',
  delegate: 'Delegate',
  shared: 'Shared with',
  approver: 'Approver',
}

// Blocked JDs as the popup lists them: { job_id, label: "REQ - Title", roles: "Owner, Approver" }
const getBlockedJds = (data) => {
  const seen = new Set()
  return (Array.isArray(data?.blocked_jds) ? data.blocked_jds : []).reduce((jds, jd) => {
    const id = jd?.job_id
    if (!id || seen.has(id)) return jds
    seen.add(id)
    const label = [jd.sb_requisition_number, jd.title].filter((part) => typeof part === 'string' && part.trim()).join(' - ')
    const roles = (Array.isArray(jd.roles) ? jd.roles : []).map((role) => JD_CONFLICT_ROLE_LABELS[role] || role).join(', ')
    jds.push({ job_id: id, label: label || id, roles })
    return jds
  }, [])
}

// Returns { message, projects, jds } for an ownership-conflict error, otherwise null.
// Items without an id are dropped since they can't be transferred.
export const getOwnershipConflict = (error) => {
  const { status, data } = error?.response ?? {}
  if (status !== OWNERSHIP_CONFLICT_STATUS || Number(data?.error_code) !== OWNERSHIP_CONFLICT_ERROR_CODE) return null

  const projectsById = new Map()
  const ownedProjects = Array.isArray(data?.owned_projects) ? data.owned_projects : []
  ownedProjects.forEach((project) => {
    const id = project?.sb_project_unique_id
    if (!id || projectsById.has(id)) return

    projectsById.set(id, {
      sb_project_unique_id: id,
      project_name: (typeof project.project_name === 'string' && project.project_name.trim()) || id,
    })
  })

  return { message: data?.message, projects: [...projectsById.values()], jds: getBlockedJds(data) }
}

// Only these core profiles may take over PAIC projects. Compared ignoring case,
// spacing and punctuation so "HR Manager" / "hr_manager" both match.
export const PAIC_OWNER_PROFILES = ['HR Manager', 'HR Executive']
const normalizeProfileName = (name) => String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')
const PAIC_OWNER_PROFILE_KEYS = new Set(PAIC_OWNER_PROFILES.map(normalizeProfileName))

const hasPAICOwnerProfile = (record) => {
  const roles = record.profile?.roles
  return Array.isArray(roles) && roles.some((role) => PAIC_OWNER_PROFILE_KEYS.has(normalizeProfileName(role)))
}

// New-owner candidates: HR Manager / HR Executive employees with an email,
// excluding the one being edited
export const buildOwnerOptions = (employeeList = [], editedEmployee) => {
  const editedUserId = editedEmployee?.userdata?.sb_user_unique_id
  const editedEmail = editedEmployee?.userdata?.email?.trim().toLowerCase()
  const seenEmails = new Set()

  return employeeList.reduce((options, record) => {
    const email = record.userdata?.email?.trim().toLowerCase()
    if (!email || email === editedEmail || seenEmails.has(email)) return options
    if (editedUserId && record.userdata?.sb_user_unique_id === editedUserId) return options
    if (!hasPAICOwnerProfile(record)) return options

    seenEmails.add(email)
    const name = `${record.userdata?.first_name ?? ''} ${record.userdata?.last_name ?? ''}`.trim()
    options.push({ value: email, label: name ? `${name} (${email})` : email })
    return options
  }, [])
}

// ─────────────────────────────────────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────────────────────────────────────

export const useRoleIdOptions = (requisitionViewData, orgMetaData) =>
  useMemo(
    () => buildRoleIdOptions(requisitionViewData, orgMetaData),
    [requisitionViewData, orgMetaData]
  )
